import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { API } from '@/config/constants';
import { useAuthenticatedMedia } from '@/hooks/useAuthenticatedMedia';
import { useArrangementPhotoOwner, arrangementPhotoIdentity } from '@/context/ArrangementPhotoContext';

// Metadata/mutations belong to Design's session owner. Bytes remain in the
// established authenticated-media/cache lifecycle.
export function useArrangementPhoto(orderId, initialPhoto, { assignedDesignerId } = {}) {
  const owner = useArrangementPhotoOwner();
  useSyncExternalStore(owner.subscribe, owner.snapshot);
  const entry = owner.entryFor(orderId, initialPhoto);
  if (assignedDesignerId !== undefined) entry.assignedDesignerId = assignedDesignerId;
  const opening = useMemo(() => ({ entry, active: false }), [entry]);
  const incoming = useRef({ entry, identity: arrangementPhotoIdentity(initialPhoto) });
  useLayoutEffect(() => {
    opening.active = true;
    const detach = owner.attach(entry);
    return () => { opening.active = false; detach(); };
  }, [owner, entry, opening]);
  useLayoutEffect(() => {
    const identity = arrangementPhotoIdentity(initialPhoto);
    if (incoming.current.entry === entry && incoming.current.identity !== identity) owner.incoming(entry, initialPhoto);
    incoming.current = { entry, identity };
  }, [owner, entry, initialPhoto]);
  const visible = owner.owns();
  const photo = visible ? entry.photo : null;
  const imageRequests = useMemo(() => photo?.storage_path ? [{
    key: 'arrangement', url: `${API}/arrangement-photo/${encodeURIComponent(photo.storage_path)}`,
  }] : [], [photo?.storage_path]);
  const mediaResult = useAuthenticatedMedia(imageRequests).arrangement;
  // The media hook retires its own URL asynchronously; a retired session or
  // confirmed deletion must hide the previous protected result immediately.
  const imageResult = visible && photo?.storage_path ? mediaResult : undefined;
  const busy = visible && entry.busy;
  const online = owner.online();
  const needsCheck = visible && ['unknown', 'checked'].includes(entry.status);
  const canRetry = online && !busy && ['rejected', 'checked'].includes(entry.status)
    && owner.eligible(entry, entry.kind) && (entry.kind === 'upload' ? !!entry.file : !!entry.photo);
  return {
    photo, imageResult, imageUrl: imageResult?.url || null, visible, busy,
    deleting: busy && entry.status === 'deleting', uploading: busy && entry.status === 'uploading',
    checking: busy && entry.status === 'checking', status: visible ? entry.status : 'retired',
    message: visible ? entry.message : '', uncertain: visible && entry.uncertain,
    operationKind: visible ? entry.kind : null,
    canUpload: online && !busy && entry.status === 'idle' && owner.eligible(entry, 'upload'),
    canDelete: online && !busy && entry.status === 'idle' && !!photo && owner.eligible(entry, 'delete'),
    canRetry, needsCheck, canCheck: online && !busy && needsCheck,
    hasSelection: visible && !!entry.file,
    upload: file => opening.active ? owner.upload(entry, file) : Promise.resolve(false),
    remove: (expectedPhoto = photo) => opening.active ? owner.remove(entry, expectedPhoto) : Promise.resolve(false),
    checkSavedPhoto: () => opening.active ? owner.check(entry) : Promise.resolve(false),
    retry: (expectedPhoto = photo) => opening.active ? owner.retry(entry, expectedPhoto) : Promise.resolve(false),
    discardSelection: () => { if (opening.active) owner.discard(entry); },
  };
}
